import React, { FC, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
// import fetchWithAuth from "../../../FetchWithAuth";
import {
  Divider,
  Card,
  Typography,
  CircularProgress,
  TableCell,
  TableRow,
  TableContainer
} from '@mui/material';
import { useModalStore } from '../../../zustandStore/store.attributes';
import { HttpModal } from '../../../components/HttpModal/HttpModal';
import { API } from '../../../config/config';
import {
  ActionCell,
  DynamicTable,
  TableSearch
} from '../../../components/DynamicTable/DynamicTable';
import { UpdateModal } from '../../../components/HttpModal/UpdateModal';
import { useUpdateModalStore } from '../../../zustandStore/store.update';
import { descendingComparator } from '../../../Utils';
import { useAuth } from 'src/hooks/useAuth';
import LogoSpinner from 'src/components/LogoSpinner';
import { CreateTagForm } from './CreateTagForm';

interface TagsProps {
  className?: string;
}

const TagsContent: FC<TagsProps> = () => {
  const { enhancedFetch } = useAuth();
  const [modalName, setModalName] = useState('');
  const state = useModalStore((state) => state);
  const action = state.actions;
  const [searchData, setSearchData] = useState(null);
  const [retry, setRetry] = useState(false);
  const deleteState = useUpdateModalStore((state) => state);
  const [sortBy, SetSortBy] = useState<string>('id');
  const [sortAsc, setSortAsc] = useState<'asc' | 'desc'>('asc');
  const [loading, setLoading] = useState(false);

  interface TagItem {
    icon?: string;
    id: string;
    tagName: string;
  }

  const [tags, setTags] = useState<TagItem[]>([]);

  // Get the Tag form
  const TagFormModal = CreateTagForm({
    onClose: () => {
      if (deleteState.settings.isEdit) {
        deleteState.actions.setModal({
          modalOpen: false,
          isEdit: false
        });
      } else {
        state.actions.setModal({
          modalOpen: false
        });

      }
    },
    onSubmit: (data) => {
      setModalName(data.tagName);
      setLoading(true);

      const isEdit = deleteState.settings.isEdit;
      const endpoint = isEdit
        ? `${API.HOST}/api/v1/tags/${deleteState.settings.payload.id}`
        : `${API.HOST}/api/v1/tags`;

      enhancedFetch(endpoint, {
        method: isEdit ? 'PUT' : 'POST',
        body: JSON.stringify({ tagName: data.tagName }),
        headers: {
          'Content-Type': 'application/json'
        }
      }).then((response) => {
        setLoading(false);
        setRetry(!retry); // Trigger a refresh of the tags list

        if (isEdit) {
          deleteState.actions.setModal({
            modalOpen: false,
            isEdit: false
          });
        } else {
          action.setModal({
            modalOpen: false
          });
        }
      }).catch((error) => {
        console.error(`Error ${isEdit ? 'updating' : 'creating'} tag:`, error);
        setLoading(false);
      });
    },
    data: {
      tagName: modalName
    },
    editData: deleteState.settings.payload,
    openModal: state.settings.modalOpen,
    editModal: deleteState.settings.isEdit,
    title: deleteState.settings.isEdit ? 'Edit Tag' : 'Create Tag',
    isLoading: loading,
    maxWidth: 'xl'
  });

  useEffect(() => {
    setLoading(true);
    enhancedFetch(`${API.HOST}/api/v1/tags`, {
      // 1. Change the URL
      method: 'GET'
    }).then((response) => {
      setLoading(false);
      if (Array.isArray(response)) {
        setTags(
          response?.map((tag: TagItem) => {
            return {
              id: tag.id,
              icon: tag.icon,
              tagName: tag.tagName
            };
          })
        );
      } else {
        console.warn("Expected type Array, got", typeof response);
      }
    });
  }, [retry]);

  // use effect hook

  useEffect(() => {
    const _tags = tags.sort((a, b) => {
      if (sortAsc === 'asc') {
        return descendingComparator(a, b, sortBy);
      }
      if (sortAsc === 'desc') {
        return -descendingComparator(a, b, sortBy);
      }
    });
    setTags(_tags);
  }, [sortBy, sortAsc]);

  const sortedTags = [...tags].sort((a, b) => {
    if (a.tagName > b.tagName) return 1; // Sort in ascending order
    if (a.tagName < b.tagName) return -1;
    return 0;
  });

  if (loading)
    return (
      <div
        style={{
          height: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <LogoSpinner />
      </div>
    );

  return (
    <Card>

      {/* Add the Modal here */}
      {TagFormModal}
      <Divider />
      <TableContainer>
        <DynamicTable
          sortByField={sortBy}
          onSort={(headerProperty) => {
            SetSortBy(headerProperty);
            setSortAsc(sortAsc === 'asc' ? 'desc' : 'asc');
          }}
          sort={sortAsc}
          searchElement={
            <TableSearch
              placeholder={'Search Staffs'}
              searchData={tags}
              setSearchData={setSearchData}
            />
          }
          rows={tags?.map((tag) => ({
            id: tag.id,
            tagName: tag.tagName
          }))}

          headers={[

            {
              headerName: 'Tags',
              field: 'tagName'
            },
            {
              headerName: 'Actions',
              field: 'actions',
              renderCell: (value) => {
                return (
                  <ActionCell
                    deleteEndpoint={`${API.HOST}/api/v1/tags/${value.row.id}`}
                    editEndpoint={`${API.HOST}/api/v1/tags/${value.row.id}`}
                    payload={tags.find((tag) => tag.id === value.row.id)}
                    callback={() => setRetry(!retry)}
                  />
                );

              }
            }
          ]}
        />
      </TableContainer>
    </Card>
  );
};

TagsContent.propTypes = {
  className: PropTypes.string
};
export default TagsContent;
